import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, ArrowRight } from "lucide-react";
import { SOLUTION_DETAILS } from "../data/solutions";
import { SERVICES } from "../data/services";
import { INDUSTRY_DETAILS } from "../data/industries";
import { ARTICLES } from "../data/articles";
import { CASE_STUDIES } from "../data/caseStudies";
import { STORE_CATEGORIES } from "../data/storeCategories";

const buildIndex = () => [
  ...SOLUTION_DETAILS.map((s) => ({ type: "Solution", title: s.name, text: s.tagline, to: `/solutions/${s.slug}`, hay: `${s.name} ${s.tagline} ${s.components.join(" ")} ${s.deliverables.join(" ")} cctv camera surveillance security` })),
  ...SERVICES.map((s) => ({ type: "Service", title: s.name, text: s.text, to: `/services/${s.slug}`, hay: `${s.name} ${s.text} ${s.includes.join(" ")}` })),
  ...INDUSTRY_DETAILS.map((i) => ({ type: "Industry", title: i.name, text: i.intro, to: `/industries/${i.slug}`, hay: `${i.name} ${i.intro} ${i.challenges.join(" ")}` })),
  ...ARTICLES.map((a) => ({ type: "Insight", title: a.title, text: a.excerpt, to: `/insights/${a.slug}`, hay: `${a.title} ${a.excerpt} ${a.category}` })),
  ...CASE_STUDIES.map((c) => ({ type: "Project", title: c.title, text: c.summary, to: `/projects/${c.slug}`, hay: `${c.title} ${c.summary} ${c.category} ${c.industry}` })),
  ...STORE_CATEGORIES.map((c) => ({
    type: "Products", title: c.name, text: c.tagline, to: `/store/${c.slug}`,
    hay: `${c.name} ${c.shortName} ${c.tagline} ${c.intro} ${c.groups.map((g) => `${g.title} ${g.items.join(" ")}`).join(" ")} ${(c.applications || []).join(" ")} price buy quote`,
  })),
];

export const SearchModal = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const index = useMemo(buildIndex, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = q.trim().length < 2 ? [] : index.filter((r) => r.hay.toLowerCase().includes(q.toLowerCase())).slice(0, 12);

  const go = (to) => {
    setOpen(false);
    setQ("");
    navigate(to);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-slate-300 hover:text-nx-goldlight transition-colors p-2" aria-label="Search" data-testid="search-open-button">
        <Search size={18} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-[60] bg-nx-navy/90 backdrop-blur-md flex items-start justify-center pt-24 sm:pt-32 px-4" onClick={() => setOpen(false)} data-testid="search-modal">
            <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -16, opacity: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 border border-white/20 bg-nx-navy2 px-5">
                <Search size={18} className="text-nx-goldlight shrink-0" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search solutions, services, industries, projects, insights…"
                  className="flex-1 bg-transparent text-slate-100 placeholder:text-slate-500 py-4 text-sm focus:outline-none"
                  data-testid="search-input"
                />
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1" aria-label="Close search" data-testid="search-close"><X size={18} /></button>
              </div>
              {q.trim().length >= 2 && (
                <div className="mt-2 border border-white/10 bg-nx-navy2 max-h-[55vh] overflow-y-auto" data-testid="search-results">
                  {results.length === 0 ? (
                    <p className="text-sm text-slate-500 px-5 py-6">No matches for "{q}".</p>
                  ) : (
                    results.map((r, i) => (
                      <button key={`${r.to}-${i}`} onClick={() => go(r.to)} className="group w-full flex items-center gap-4 px-5 py-4 text-left border-b border-white/5 hover:bg-white/[0.04] transition-colors" data-testid={`search-result-${i}`}>
                        <span className="text-[10px] tracking-[0.18em] uppercase text-nx-goldlight border border-nx-gold/30 px-2 py-1 shrink-0 w-20 text-center">{r.type}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-white truncate">{r.title}</span>
                          <span className="block text-xs text-slate-500 truncate mt-0.5">{r.text}</span>
                        </span>
                        <ArrowRight size={14} className="text-slate-600 group-hover:text-nx-goldlight shrink-0 transition-colors" />
                      </button>
                    ))
                  )}
                </div>
              )}
              <p className="text-center text-[11px] text-slate-600 mt-4">Press Esc to close</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
